import type { HomeCopy, SupportedMarketingLocale } from "./types";

/**
 * Home page copy. Keys are split granularly so the headline can be rebuilt
 * with the same `<span class="text-primary">` highlights in both languages.
 */
export const home: Record<SupportedMarketingLocale, HomeCopy> = {
  en: {
    seo: {
      title: "Vote.GameOn.Futbol — RSVP, Player Voting & RPE for grassroots football",
      description:
        "Frictionless platform for grassroots football coaches: WhatsApp-native game RSVP, player-of-the-match voting, and pro-grade exertion tracking. No app for players or parents.",
    },
    hero: {
      eyebrow: "Player's Player Polls, Simplified",
      headlineLine1: "Zero Friction for ",
      headlinePrimary: "Players",
      headlineLine2: ",",
      headlineFreePrimary: "Free",
      headlineLine2After: " for coaches",
      subhead: "Only coaches need accounts. Players vote with just one click.",
      bullet1: "Players vote without an account",
      bullet2: "No player accounts needed",
      bullet3: "Simple one-click voting",
      cta: "Start an MVP Vote",
      linkRsvp: "See how RSVP works →",
      linkRpe: "See how Exertion tracking works →",
      linkFeedback: "See how Coach Feedback works →",
      worksWith: "Shares to your team's chat app",
    },
    howItWorks: {
      heading: "How Player's Player Polls work",
      subhead: "Fast, frictionless, voting for your team",
      step1Title: "Create a Poll",
      step1Body:
        "Set up your team, add players, and create a custom poll for your next game.",
      step2Title: "Share the Link",
      step2Body:
        "Send the private link to your team — no accounts needed. They simply click and vote.",
      step3Title: "Collect & Celebrate",
      step3Body:
        "Track votes, view results, and recognise outstanding performances in your team.",
    },
    whyItWorks: {
      heading: "Why It Works",
      subhead: "Research-backed tools designed for youth sport development",
      card1Title: "Peer Recognition, Not Coach Awards",
      card1Body:
        "When teammates vote for each other, recognition feels authentic. Rooted in Self-Determination Theory, peer votes nurture intrinsic motivation far more than top-down awards.",
      card2Title: "Effort Over Outcomes",
      card2Body:
        "We encourage voting for controllable behaviours — effort, attitude, teamwork — not just goals scored. This growth-mindset approach builds resilient, motivated players.",
      card3Title: "Science-Based Load Monitoring",
      card3Body:
        "Using the Borg CR-10 scale and Foster's Session-RPE method, coaches can track training load and protect player welfare with a simple post-session rating.",
      card4Title: "Training Attendance Made Easy",
      card4Body:
        "One-tap roll calls, smart training-day prompts, and unified attendance charts let coaches track who shows up — building accountability and team culture.",
      learnMore: "Learn more",
      methodologyLink: "Read our research-backed methodology",
    },
  },
  es: {
    seo: {
      title:
        "Vote.GameOn.Futbol — Asistencia, Votación de Jugadores y RPE para fútbol amateur",
      description:
        "Plataforma sin fricción para entrenadores de fútbol amateur: confirmación de asistencia desde WhatsApp, votación al jugador del partido y monitoreo de carga de pro. Sin apps para jugadores ni padres.",
    },
    hero: {
      eyebrow: "Encuestas del jugador del partido, simplificadas",
      headlineLine1: "Cero fricción para los ",
      headlinePrimary: "jugadores",
      headlineLine2: ",",
      headlineFreePrimary: "gratis",
      headlineLine2After: " para entrenadores",
      subhead:
        "Solo el entrenador necesita cuenta. Los jugadores votan con un toque.",
      bullet1: "Los jugadores votan sin cuenta",
      bullet2: "Sin cuentas para jugadores",
      bullet3: "Votación con un toque",
      cta: "Crear una encuesta",
      linkRsvp: "Cómo funciona la asistencia →",
      linkRpe: "Cómo funciona el monitoreo de carga →",
      linkFeedback: "Cómo funciona la evaluación del entrenador →",
      worksWith: "Comparte al chat de tu equipo",
    },
    howItWorks: {
      heading: "Cómo funcionan las encuestas",
      subhead: "Votación rápida y sin fricción para tu equipo",
      step1Title: "Crea la encuesta",
      step1Body:
        "Arma tu equipo, agrega jugadores y crea una encuesta personalizada para el próximo partido.",
      step2Title: "Comparte el enlace",
      step2Body:
        "Envía el enlace privado al equipo — sin cuentas. Solo tocan y votan.",
      step3Title: "Reconoce y celebra",
      step3Body:
        "Sigue los votos en vivo, mira los resultados y celebra a los jugadores que se destacaron.",
    },
    whyItWorks: {
      heading: "Por qué funciona",
      subhead:
        "Herramientas con respaldo científico, pensadas para el desarrollo deportivo juvenil",
      card1Title: "Reconocimiento entre pares, no premios del entrenador",
      card1Body:
        "Cuando los compañeros votan entre ellos, el reconocimiento se siente auténtico. Basado en la Teoría de la Autodeterminación, los votos de los pares fortalecen la motivación intrínseca mucho más que los premios verticales.",
      card2Title: "Esfuerzo por encima del resultado",
      card2Body:
        "Promovemos votar por conductas controlables — esfuerzo, actitud, trabajo en equipo — no solo los goles. Este enfoque de mentalidad de crecimiento forma jugadores motivados y resilientes.",
      card3Title: "Monitoreo de carga con base científica",
      card3Body:
        "Con la escala de Borg CR-10 y el método sRPE de Foster, los entrenadores pueden seguir la carga de entrenamiento y cuidar a sus jugadores con una simple valoración después de cada sesión.",
      card4Title: "Asistencia a entrenamientos sin complicaciones",
      card4Body:
        "Pase de lista con un toque, recordatorios automáticos en días de entrenamiento y gráficos unificados — para que sepas quién entrena y construyas cultura de equipo.",
      learnMore: "Saber más",
      methodologyLink: "Lee nuestra metodología con respaldo científico",
    },
  },
  "pt-BR": {
    seo: {
      title: "Vote.GameOn.Futbol — Presença, Votação dos Jogadores e PSE para o futebol de base",
      description:
        "Plataforma sem atrito para treinadores do futebol de base: confirmação de presença pelo WhatsApp, votação no craque do jogo e monitoramento de carga profissional. Sem app para jogadores ou pais.",
    },
    hero: {
      eyebrow: "Enquetes do craque do jogo, simplificadas",
      headlineLine1: "Zero atrito para os ",
      headlinePrimary: "jogadores",
      headlineLine2: ",",
      headlineFreePrimary: "grátis",
      headlineLine2After: " para os treinadores",
      subhead:
        "Só o treinador precisa de conta. Os jogadores votam com um toque.",
      bullet1: "Os jogadores votam sem conta",
      bullet2: "Sem contas para jogadores",
      bullet3: "Votação com um toque",
      cta: "Criar uma enquete",
      linkRsvp: "Como funciona a presença →",
      linkRpe: "Como funciona o monitoramento de carga →",
      linkFeedback: "Como funciona a avaliação do treinador →",
      worksWith: "Compartilha no chat do seu time",
    },
    howItWorks: {
      heading: "Como funcionam as enquetes",
      subhead: "Votação rápida e sem atrito para o seu time",
      step1Title: "Crie a enquete",
      step1Body:
        "Monte o time, adicione os jogadores e crie uma enquete personalizada para o próximo jogo.",
      step2Title: "Compartilhe o link",
      step2Body:
        "Envie o link privado ao time — sem contas. Eles só tocam e votam.",
      step3Title: "Reconheça e celebre",
      step3Body:
        "Acompanhe os votos ao vivo, veja os resultados e celebre quem se destacou.",
    },
    whyItWorks: {
      heading: "Por que funciona",
      subhead:
        "Ferramentas com base científica, pensadas para o desenvolvimento esportivo juvenil",
      card1Title: "Reconhecimento entre colegas, não prêmios do treinador",
      card1Body:
        "Quando os colegas votam uns nos outros, o reconhecimento parece autêntico. Baseado na Teoria da Autodeterminação, os votos dos pares fortalecem a motivação intrínseca muito mais que prêmios verticais.",
      card2Title: "Esforço acima do resultado",
      card2Body:
        "Incentivamos votar em comportamentos controláveis — esforço, atitude, trabalho em equipe — não só os gols. É uma abordagem de mentalidade de crescimento que forma jogadores resilientes.",
      card3Title: "Monitoramento de carga com base científica",
      card3Body:
        "Com a escala de Borg CR-10 e o método sRPE de Foster, treinadores acompanham a carga de treino e cuidam dos jogadores com uma simples avaliação após cada sessão.",
      card4Title: "Presença em treinos sem complicação",
      card4Body:
        "Chamadas com um toque, lembretes inteligentes nos dias de treino e gráficos unificados — para você saber quem treina e construir cultura de equipe.",
      learnMore: "Saiba mais",
      methodologyLink: "Leia nossa metodologia com respaldo científico",
    },
  },
  fr: {
    seo: {
      title:
        "Vote.GameOn.Futbol — Présence, vote des joueurs et RPE pour le football amateur",
      description:
        "Plateforme sans friction pour les entraîneurs amateurs : confirmation de présence dans WhatsApp, vote du joueur du match et suivi de charge professionnel. Aucune app pour les joueurs ou les parents.",
    },
    hero: {
      eyebrow: "Sondages du joueur du match, simplifiés",
      headlineLine1: "Zéro friction pour les ",
      headlinePrimary: "joueurs",
      headlineLine2: ",",
      headlineFreePrimary: "gratuit",
      headlineLine2After: " pour les entraîneurs",
      subhead:
        "Seul l'entraîneur a besoin d'un compte. Les joueurs votent en un clic.",
      bullet1: "Les joueurs votent sans compte",
      bullet2: "Aucun compte joueur",
      bullet3: "Vote en un clic",
      cta: "Créer un sondage",
      linkRsvp: "Voir la confirmation de présence →",
      linkRpe: "Voir le suivi de charge (RPE) →",
      linkFeedback: "Voir l'évaluation de l'entraîneur →",
      worksWith: "Partage vers le chat de votre équipe",
    },
    howItWorks: {
      heading: "Comment fonctionnent les sondages",
      subhead: "Un vote rapide et sans friction pour votre équipe",
      step1Title: "Créez le sondage",
      step1Body:
        "Constituez votre équipe, ajoutez les joueurs et créez un sondage sur mesure pour le prochain match.",
      step2Title: "Partagez le lien",
      step2Body:
        "Envoyez le lien privé à votre équipe — pas de compte. Ils cliquent et votent.",
      step3Title: "Reconnaissez et célébrez",
      step3Body:
        "Suivez les votes en direct, consultez les résultats et célébrez les joueurs qui se sont distingués.",
    },
    whyItWorks: {
      heading: "Pourquoi ça marche",
      subhead:
        "Des outils fondés sur la recherche, pensés pour le développement sportif des jeunes",
      card1Title: "Reconnaissance entre pairs, pas un prix de l'entraîneur",
      card1Body:
        "Quand les coéquipiers votent les uns pour les autres, la reconnaissance paraît authentique. Fondés sur la théorie de l'autodétermination, les votes des pairs renforcent la motivation intrinsèque bien plus que les récompenses descendantes.",
      card2Title: "L'effort avant le résultat",
      card2Body:
        "Nous encourageons à voter pour des comportements maîtrisables — l'effort, l'attitude, le jeu collectif — pas seulement les buts. Une approche d'état d'esprit de croissance qui forge des joueurs motivés et résilients.",
      card3Title: "Suivi de charge fondé sur la science",
      card3Body:
        "Avec l'échelle de Borg CR-10 et la méthode sRPE de Foster, les entraîneurs suivent la charge d'entraînement et préservent leurs joueurs avec une simple évaluation après chaque séance.",
      card4Title: "Présence aux entraînements sans tracas",
      card4Body:
        "Appel en un clic, rappels intelligents les jours d'entraînement et graphiques unifiés — pour savoir qui s'entraîne et bâtir une culture d'équipe.",
      learnMore: "En savoir plus",
      methodologyLink: "Lisez notre méthodologie fondée sur la recherche",
    },
  },
  ja: {
    seo: {
      title: "Vote.GameOn.Futbol — 草の根フットボールのための出欠確認・選手投票・RPE",
      description:
        "草の根フットボールのコーチのための摩擦ゼロのプラットフォーム。WhatsApp/LINEでそのまま使える出欠確認、MVP投票、プロ仕様の運動負荷トラッキング。選手や保護者にアプリは不要です。",
    },
    hero: {
      eyebrow: "選手間投票を、もっとシンプルに",
      headlineLine1: "",
      headlinePrimary: "選手",
      headlineLine2: "は摩擦ゼロ、",
      headlineFreePrimary: "コーチ",
      headlineLine2After: "は完全無料",
      subhead: "アカウントが必要なのはコーチだけ。選手はワンタップで投票できます。",
      bullet1: "選手はアカウントなしで投票",
      bullet2: "選手アカウント不要",
      bullet3: "ワンタップで投票",
      cta: "MVP投票を始める",
      linkRsvp: "出欠確認の仕組みを見る →",
      linkRpe: "運動負荷トラッキングを見る →",
      linkFeedback: "コーチ評価の仕組みを見る →",
      worksWith: "チームのチャットアプリにそのまま共有",
    },
    howItWorks: {
      heading: "選手間投票の使い方",
      subhead: "チームのために、素早く摩擦のない投票を",
      step1Title: "投票を作成",
      step1Body:
        "チームを設定し、選手を追加し、次の試合用の投票を作成します。",
      step2Title: "リンクを共有",
      step2Body:
        "チームにプライベートリンクを送るだけ。アカウント不要。タップして投票するだけです。",
      step3Title: "集計して称える",
      step3Body:
        "投票をリアルタイムで確認し、結果を見て、活躍した選手をチームで称えましょう。",
    },
    whyItWorks: {
      heading: "なぜ機能するのか",
      subhead: "青少年スポーツの育成のために設計された、研究に裏付けされたツール",
      card1Title: "コーチの表彰ではなく、仲間からの評価",
      card1Body:
        "仲間同士で投票し合うと、評価は本物に感じられます。自己決定理論に基づき、仲間からの投票はトップダウンの表彰よりも内発的動機を大きく育てます。",
      card2Title: "結果よりもプロセス",
      card2Body:
        "得点だけでなく、努力・態度・チームワークといった自分でコントロールできる行動への投票を推奨します。この成長マインドセット型の考え方が、粘り強く意欲のある選手を育てます。",
      card3Title: "科学に基づく負荷モニタリング",
      card3Body:
        "ボルグCR-10スケールとFosterのSession-RPE法を使い、練習後の簡単な評価だけで負荷を追跡し、選手の健康を守れます。",
      card4Title: "練習出欠を、もっとシンプルに",
      card4Body:
        "ワンタップの点呼、練習日のスマートなリマインダー、まとめて見える出席グラフ。誰が来ているかが分かり、責任感とチーム文化を育てます。",
      learnMore: "詳しく見る",
      methodologyLink: "研究に基づく方法論を読む",
    },
  },
};
